const GMT_PLUS_3_TIME_ZONE = 'Europe/Moscow';
const ISO_DATE_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;
const GMT_PLUS_3_OFFSET_IN_MS = 3 * 60 * 60 * 1000;
const DAY_IN_MS = 24 * 60 * 60 * 1000;
const MODERN_GMT_PLUS_3_START_UTC_MS = Date.UTC(2015, 0, 1);
const ISO_TIMESTAMP_WITH_EXPLICIT_OFFSET_PATTERN =
    /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(?::\d{2}(?:\.\d{1,})?)?(?:Z|[+-]\d{2}:?\d{2})$/;

type DateInput = string | Date | null | undefined;
type GmtPlus3DateTimeParts = { isoDate: string; ruDate: string; time: string };

const gmtPlus3DateFormatter = new Intl.DateTimeFormat('en-US', {
    timeZone: GMT_PLUS_3_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
});

const gmtPlus3RuDateFormatter = new Intl.DateTimeFormat('ru-RU', {
    timeZone: GMT_PLUS_3_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
});

const gmtPlus3RuTimeFormatter = new Intl.DateTimeFormat('ru-RU', {
    timeZone: GMT_PLUS_3_TIME_ZONE,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
});

const parseDateInput = (value: DateInput): Date | null => {
    if (value instanceof Date) {
        return Number.isNaN(value.getTime()) ? null : value;
    }

    if (typeof value !== 'string' || !value.trim()) {
        return null;
    }

    const parsedDate = new Date(value);
    return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
};

const pad2 = (value: number): string => value.toString().padStart(2, '0');

const parseTwoDigits = (value: string, start: number): number | null => {
    const first = value.charCodeAt(start) - 48;
    const second = value.charCodeAt(start + 1) - 48;

    if (first < 0 || first > 9 || second < 0 || second > 9) {
        return null;
    }

    return first * 10 + second;
};

const parseFourDigits = (value: string, start: number): number | null => {
    const firstPair = parseTwoDigits(value, start);
    const secondPair = parseTwoDigits(value, start + 2);

    if (firstPair === null || secondPair === null) {
        return null;
    }

    return firstPair * 100 + secondPair;
};

const isLeapYear = (year: number): boolean => {
    return year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0);
};

const getDaysInMonth = (year: number, month: number): number => {
    if (month === 2) {
        return isLeapYear(year) ? 29 : 28;
    }

    return month === 4 || month === 6 || month === 9 || month === 11 ? 30 : 31;
};

const hasValidOptionalSecondsAndFraction = (value: string): boolean => {
    if (value.charAt(16) === 'Z') {
        return value.length === 17;
    }

    if (value.charAt(16) !== ':') {
        return false;
    }

    const seconds = parseTwoDigits(value, 17);
    if (seconds === null || seconds > 59) {
        return false;
    }

    if (value.charAt(19) === 'Z') {
        return value.length === 20;
    }

    if (value.charAt(19) !== '.') {
        return false;
    }

    for (let index = 20; index < value.length - 1; index += 1) {
        const digit = value.charCodeAt(index) - 48;
        if (digit < 0 || digit > 9) {
            return false;
        }
    }

    return value.length > 21 && value.charAt(value.length - 1) === 'Z';
};

const getModernUtcIsoStringPartsInGmtPlus3 = (value: string): GmtPlus3DateTimeParts | null => {
    if (
        value.length < 17 ||
        value.charAt(value.length - 1) !== 'Z' ||
        value.charAt(4) !== '-' ||
        value.charAt(7) !== '-' ||
        value.charAt(10) !== 'T' ||
        value.charAt(13) !== ':' ||
        !hasValidOptionalSecondsAndFraction(value)
    ) {
        return null;
    }

    const yearNumber = parseFourDigits(value, 0);
    const monthNumber = parseTwoDigits(value, 5);
    const dayNumber = parseTwoDigits(value, 8);
    const hourNumber = parseTwoDigits(value, 11);
    const minuteNumber = parseTwoDigits(value, 14);

    if (
        yearNumber === null ||
        monthNumber === null ||
        dayNumber === null ||
        hourNumber === null ||
        minuteNumber === null ||
        yearNumber < 2015 ||
        monthNumber < 1 ||
        monthNumber > 12 ||
        dayNumber < 1 ||
        dayNumber > getDaysInMonth(yearNumber, monthNumber) ||
        hourNumber > 23 ||
        minuteNumber > 59
    ) {
        return null;
    }

    if (hourNumber <= 20) {
        const year = value.slice(0, 4);
        const month = value.slice(5, 7);
        const day = value.slice(8, 10);
        const hour = pad2(hourNumber + 3);
        const minute = value.slice(14, 16);

        return {
            isoDate: `${year}-${month}-${day}`,
            ruDate: `${day}.${month}.${year}`,
            time: `${hour}:${minute}`,
        };
    }

    const gmtPlus3Date = new Date(Date.UTC(yearNumber, monthNumber - 1, dayNumber, hourNumber + 3, minuteNumber));
    const year = gmtPlus3Date.getUTCFullYear().toString().padStart(4, '0');
    const month = pad2(gmtPlus3Date.getUTCMonth() + 1);
    const day = pad2(gmtPlus3Date.getUTCDate());
    const hour = pad2(gmtPlus3Date.getUTCHours());
    const minute = pad2(gmtPlus3Date.getUTCMinutes());

    return {
        isoDate: `${year}-${month}-${day}`,
        ruDate: `${day}.${month}.${year}`,
        time: `${hour}:${minute}`,
    };
};

const getModernIsoStringPartsInGmtPlus3 = (value: DateInput): GmtPlus3DateTimeParts | null => {
    if (typeof value !== 'string') {
        return null;
    }

    const utcIsoParts = getModernUtcIsoStringPartsInGmtPlus3(value);
    if (utcIsoParts) {
        return utcIsoParts;
    }

    if (!ISO_TIMESTAMP_WITH_EXPLICIT_OFFSET_PATTERN.test(value)) {
        return null;
    }

    const timestampMs = Date.parse(value);
    if (Number.isNaN(timestampMs) || timestampMs < MODERN_GMT_PLUS_3_START_UTC_MS) {
        return null;
    }

    const gmtPlus3Date = new Date(timestampMs + 3 * 60 * 60 * 1000);
    const year = gmtPlus3Date.getUTCFullYear().toString().padStart(4, '0');
    const month = pad2(gmtPlus3Date.getUTCMonth() + 1);
    const day = pad2(gmtPlus3Date.getUTCDate());
    const hour = pad2(gmtPlus3Date.getUTCHours());
    const minute = pad2(gmtPlus3Date.getUTCMinutes());

    return {
        isoDate: `${year}-${month}-${day}`,
        ruDate: `${day}.${month}.${year}`,
        time: `${hour}:${minute}`,
    };
};

const getIsoDateParts = (value: DateInput): { year: string; month: string; day: string } | null => {
    const fastParts = getModernIsoStringPartsInGmtPlus3(value);
    if (fastParts) {
        const [year, month, day] = fastParts.isoDate.split('-');
        return { year, month, day };
    }

    const parsedDate = parseDateInput(value);
    if (!parsedDate) return null;

    const parts = gmtPlus3DateFormatter.formatToParts(parsedDate);
    const year = parts.find((part) => part.type === 'year')?.value;
    const month = parts.find((part) => part.type === 'month')?.value;
    const day = parts.find((part) => part.type === 'day')?.value;

    if (!year || !month || !day) {
        return null;
    }

    return { year, month, day };
};

export const normalizeIsoDateFilter = (value: string | null | undefined): string | null => {
    if (!value) return null;

    const match = ISO_DATE_PATTERN.exec(value);
    if (!match) return null;

    const year = Number(match[1]);
    const month = Number(match[2]);
    const day = Number(match[3]);
    const parsed = new Date(Date.UTC(year, month - 1, day));

    if (parsed.getUTCFullYear() !== year || parsed.getUTCMonth() !== month - 1 || parsed.getUTCDate() !== day) {
        return null;
    }

    return `${match[1]}-${match[2]}-${match[3]}`;
};

const toDateParts = (value: string): [number, number, number] => {
    return value.split('-').map((part) => Number(part)) as [number, number, number];
};

export const toUtcStartIsoFromGmtPlus3Date = (value: string): string => {
    const [year, month, day] = toDateParts(value);
    const startUtcTimestamp = Date.UTC(year, month - 1, day) - GMT_PLUS_3_OFFSET_IN_MS;
    return new Date(startUtcTimestamp).toISOString();
};

export const toUtcExclusiveEndIsoFromGmtPlus3Date = (value: string): string => {
    const [year, month, day] = toDateParts(value);
    const startUtcTimestamp = Date.UTC(year, month - 1, day) - GMT_PLUS_3_OFFSET_IN_MS;
    return new Date(startUtcTimestamp + DAY_IN_MS).toISOString();
};

export const toUtcInclusiveEndIsoFromGmtPlus3Date = (value: string): string => {
    const exclusiveEndTimestamp = Date.parse(toUtcExclusiveEndIsoFromGmtPlus3Date(value));
    return new Date(exclusiveEndTimestamp - 1).toISOString();
};

export const toIsoDateInGmtPlus3 = (value: DateInput): string | null => {
    const parts = getIsoDateParts(value);
    if (!parts) return null;

    return `${parts.year}-${parts.month}-${parts.day}`;
};

export const formatDateTimePartsInGmtPlus3 = (value: DateInput): { date: string; time: string } | null => {
    const fastParts = getModernIsoStringPartsInGmtPlus3(value);
    if (fastParts) {
        return {
            date: fastParts.ruDate,
            time: fastParts.time,
        };
    }

    const parsedDate = parseDateInput(value);
    if (!parsedDate) return null;

    return {
        date: gmtPlus3RuDateFormatter.format(parsedDate),
        time: gmtPlus3RuTimeFormatter.format(parsedDate),
    };
};

export const formatDateTimeInGmtPlus3 = (value: DateInput): string | null => {
    const parts = formatDateTimePartsInGmtPlus3(value);
    if (!parts) return null;

    return `${parts.date} ${parts.time}`;
};

export const matchesIsoDateFilterInGmtPlus3 = (value: DateInput, selectedDate: string | undefined): boolean => {
    if (!selectedDate) return true;

    const normalizedSelectedDate = normalizeIsoDateFilter(selectedDate);
    if (!normalizedSelectedDate) return false;

    const reviewDate = toIsoDateInGmtPlus3(value);
    if (!reviewDate) return false;

    return reviewDate === normalizedSelectedDate;
};
